/**
 * AudioGuide — portal-wide text-to-speech narration for help content.
 *
 *   AudioGuideProvider   — mount ONCE in App.js. Owns the narration engine
 *                          and renders the floating mini-player so audio
 *                          keeps playing after a help window closes and
 *                          across page navigation.
 *   useAudioGuide()      — context hook: startGuide / pause / seek / etc.
 *   <ListenButton />     — drop-in "🎧 Listen" button for any help modal.
 *   extractSectionsFromElement — DOM → narration-script converter.
 *
 * Dual-host (NotebookLM-style): Clara + Liam trade turns. Voices are fixed
 * to Azure en-CA Clara / Liam (browser fallback picks closest female/male).
 *
 * Engines (preferred → fallback):
 *   1. Azure Neural TTS via /api/audio-guide/synthesize (disk-cached MP3).
 *   2. Browser Web Speech API when Azure is unavailable.
 *
 * French narration is a follow-up (prefs keep lang: 'en' ready).
 */

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { getHelpNarration, buildDualHostQueue, speakerLabel } from './helpNarrations';

const DEFAULT_ACCENT = '#0f766e';
const PREFS_KEY = 'lgl-audio-guide-prefs';
const SENTENCE_GAP_MS = 280;
const SECTION_GAP_MS = 950;

const AZURE_VOICES = {
  female: { name: 'en-CA-ClaraNeural', label: 'Clara', gender: 'female', natural: true },
  male: { name: 'en-CA-LiamNeural', label: 'Liam', gender: 'male', natural: true },
};

/* ------------------------------------------------------------------ */
/* Speech text utilities                                               */
/* ------------------------------------------------------------------ */

export const normalizeSpeech = (text) => String(text || '')
  .replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2B00}-\u{2BFF}\u{FE0F}\u{200D}\u{2190}-\u{21FF}✓✗×÷≈⇒—–]/gu, (m) => ({
    '×': ' times ', '÷': ' divided by ', '≈': ' about ', '—': ', ', '–': ', ',
    '→': ' then ', '⇒': ' then ', '✓': '', '✗': '',
  }[m] ?? ' '))
  .replace(/&/g, ' and ')
  .replace(/\s+/g, ' ')
  .trim();

const endPunct = (t) => (/[.!?:]$/.test(t) ? t : `${t}.`);

/**
 * Walk a rendered help modal and build a narration script from it.
 * Headings (h1–h4, or any element with data-audio-title) start a new
 * section; buttons/inputs/code/kbd and anything with data-audio-skip are
 * ignored. Returns [{ title, speech }].
 */
export function extractSectionsFromElement(root, fallbackTitle = 'Guide') {
  if (!root) return [];
  const SKIP_TAGS = new Set(['BUTTON', 'INPUT', 'SELECT', 'TEXTAREA', 'CODE', 'KBD', 'SVG', 'STYLE', 'SCRIPT', 'IMG', 'CANVAS']);
  const CONTAINER_TAGS = new Set(['DIV', 'SECTION', 'ARTICLE', 'MAIN', 'ASIDE', 'UL', 'OL', 'TABLE', 'TBODY', 'THEAD', 'TR', 'FORM', 'NAV', 'HEADER', 'FOOTER', 'FIELDSET']);

  const sections = [];
  let current = { title: fallbackTitle, speech: '' };
  const flush = () => {
    const speech = normalizeSpeech(current.speech);
    if (speech) sections.push({ title: normalizeSpeech(current.title) || fallbackTitle, speech });
  };

  const collect = (text) => {
    if (!/\w{2,}/.test(text || '')) return;
    const t = normalizeSpeech(text);
    if (t) current.speech += `${endPunct(t)} `;
  };

  const isHeading = (el) => /^H[1-4]$/.test(el.tagName) || el.hasAttribute('data-audio-title');
  const hasStructure = (el) => Array.from(el.children).some(
    (c) => CONTAINER_TAGS.has(c.tagName) || isHeading(c) || c.tagName === 'P' || c.tagName === 'LI',
  );

  const walk = (el) => {
    if (!el || el.nodeType !== 1) return;
    if (SKIP_TAGS.has(el.tagName) || el.hasAttribute('data-audio-skip')) return;
    if (isHeading(el)) {
      flush();
      current = { title: el.textContent, speech: '' };
      return;
    }
    if (hasStructure(el)) {
      Array.from(el.children).forEach(walk);
    } else {
      collect(el.textContent);
    }
  };

  Array.from(root.children).forEach(walk);
  flush();
  return sections;
}

/* ------------------------------------------------------------------ */
/* Browser voice selection (fallback engine)                           */
/* ------------------------------------------------------------------ */

const FEMALE_HINTS = ['aria', 'jenny', 'emma', 'michelle', 'ava', 'sonia', 'libby', 'natasha', 'clara', 'zira', 'samantha', 'susan', 'hazel', 'female'];
const MALE_HINTS = ['guy', 'andrew', 'brian', 'christopher', 'eric', 'roger', 'steffan', 'ryan', 'thomas', 'david', 'mark', 'george', 'james', 'alex', 'daniel', 'male', 'liam'];

function pickVoice(voices, gender) {
  const english = voices.filter((v) => /^en(-|_)/i.test(v.lang));
  const pool = english.length ? english : voices;
  const hints = gender === 'male' ? MALE_HINTS : FEMALE_HINTS;
  const named = pool.filter((v) => hints.some((h) => v.name.toLowerCase().includes(h)));
  const rank = (v) => {
    const n = v.name.toLowerCase();
    if (n.includes('natural') || n.includes('neural') || n.includes('online')) return 0;
    if (n.includes('google')) return 1;
    if (n.includes('microsoft')) return 2;
    return 3;
  };
  const sorted = (named.length ? named : pool).slice().sort((a, b) => rank(a) - rank(b));
  return sorted[0] || null;
}

/* ------------------------------------------------------------------ */
/* Provider / engine                                                   */
/* ------------------------------------------------------------------ */

const AudioGuideContext = createContext(null);

const loadPrefs = () => {
  try { return JSON.parse(window.localStorage.getItem(PREFS_KEY)) || {}; } catch { return {}; }
};
const savePrefs = (p) => {
  try { window.localStorage.setItem(PREFS_KEY, JSON.stringify(p)); } catch { /* private mode */ }
};

export function AudioGuideProvider({ children }) {
  const { instance, accounts } = useMsal();
  const browserSupported = typeof window !== 'undefined' && 'speechSynthesis' in window;

  const [azureConfigured, setAzureConfigured] = useState(false);
  const [guide, setGuide] = useState(null);
  const [status, setStatus] = useState('idle');
  const [visible, setVisible] = useState(false);
  const [idx, setIdx] = useState(0);
  const [intra, setIntra] = useState(0);
  const [rate, setRate] = useState(() => loadPrefs().rate || 1);
  // hostMode: duo (NotebookLM both hosts) | clara | liam (solo override)
  const [hostMode, setHostMode] = useState(() => {
    const p = loadPrefs();
    if (p.hostMode === 'clara' || p.hostMode === 'liam' || p.hostMode === 'duo') return p.hostMode;
    return 'duo';
  });
  const [voices, setVoices] = useState([]);

  const queueRef = useRef([]);
  const gapTimerRef = useRef(null);
  const sessionRef = useRef(0);
  const audioRef = useRef(null);
  const blobCacheRef = useRef(new Map()); // key -> object URL
  const prefetchRef = useRef(null);
  const voicesRef = useRef([]);
  const stateRef = useRef({
    idx: 0, rate, hostMode, playing: false, azure: false,
  });
  stateRef.current.rate = rate;
  stateRef.current.hostMode = hostMode;
  stateRef.current.azure = azureConfigured;
  voicesRef.current = voices;

  const msalRef = useRef({ instance, accounts });
  msalRef.current = { instance, accounts };

  const supported = azureConfigured || browserSupported;

  // Probe Azure TTS availability once the user is signed in.
  useEffect(() => {
    if (!accounts?.length) {
      setAzureConfigured(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await authenticatedApiCall(instance, accounts, 'get', '/audio-guide/status');
        if (!cancelled) setAzureConfigured(!!res.data?.configured);
      } catch {
        if (!cancelled) setAzureConfigured(false);
      }
    })();
    return () => { cancelled = true; };
  }, [instance, accounts]);

  useEffect(() => {
    if (!browserSupported) return undefined;
    const load = () => setVoices(window.speechSynthesis.getVoices());
    load();
    window.speechSynthesis.addEventListener('voiceschanged', load);
    return () => window.speechSynthesis.removeEventListener('voiceschanged', load);
  }, [browserSupported]);

  const hostModeOptions = useMemo(() => [
    { id: 'duo', label: 'Clara & Liam', hint: 'Dual host · NotebookLM style', gender: 'female' },
    { id: 'clara', label: 'Clara only', hint: 'Solo · female', gender: 'female' },
    { id: 'liam', label: 'Liam only', hint: 'Solo · male', gender: 'male' },
  ], []);

  // Azure path: only Clara / Liam. Browser fallback still uses duo/solo modes
  // (mapped to best installed female/male) — never a free-form OS voice picker.
  const voiceOptions = hostModeOptions;

  const stopAudioElement = useCallback(() => {
    const a = audioRef.current;
    if (a) {
      a.onended = null;
      a.onerror = null;
      a.ontimeupdate = null;
      try { a.pause(); } catch { /* ignore */ }
      a.removeAttribute('src');
      try { a.load(); } catch { /* ignore */ }
      audioRef.current = null;
    }
  }, []);

  const clearBlobCache = useCallback(() => {
    blobCacheRef.current.forEach((url) => {
      try { URL.revokeObjectURL(url); } catch { /* ignore */ }
    });
    blobCacheRef.current.clear();
  }, []);

  const resolveHostForItem = useCallback((item) => {
    const mode = stateRef.current.hostMode || 'duo';
    if (mode === 'liam') return 'liam';
    if (mode === 'clara') return 'clara';
    if (item?.speaker === 'liam' || item?.gender === 'male') return 'liam';
    return 'clara';
  }, []);

  const fetchAzureUrl = useCallback(async (sentence, host) => {
    const hostId = host === 'liam' ? 'liam' : 'clara';
    const genderForSynth = hostId === 'liam' ? 'male' : 'female';
    const key = `${hostId}|${sentence}`;
    const hit = blobCacheRef.current.get(key);
    if (hit) return hit;

    const { instance: inst, accounts: accts } = msalRef.current;
    if (!accts?.length) throw new Error('Not signed in');

    const res = await authenticatedApiCall(
      inst,
      accts,
      'post',
      // host in query avoids any intermediary mixing Clara/Liam by bare URL
      `/audio-guide/synthesize?host=${hostId}`,
      { text: sentence, gender: genderForSynth, host: hostId },
      { responseType: 'arraybuffer', timeout: 45000 },
    );

    const returned = String(res.headers?.['x-audio-guide-host'] || res.headers?.['X-Audio-Guide-Host'] || '').toLowerCase();
    if (returned && returned !== hostId) {
      throw new Error(`Voice mismatch: requested ${hostId}, got ${returned}`);
    }

    const blob = new Blob([res.data], { type: 'audio/mpeg' });
    const url = URL.createObjectURL(blob);
    blobCacheRef.current.set(key, url);
    return url;
  }, []);

  const prefetchAzure = useCallback((sentence, host) => {
    if (!sentence) return;
    const hostId = host === 'liam' ? 'liam' : 'clara';
    const key = `${hostId}|${sentence}`;
    if (blobCacheRef.current.has(key)) return;
    prefetchRef.current = fetchAzureUrl(sentence, hostId).catch(() => null);
  }, [fetchAzureUrl]);

  /** Speak a single sentence via Web Speech, then invoke onDone (does NOT continue the guide). */
  const speakBrowserSentence = useCallback((item, session, onDone) => {
    if (!browserSupported || !item) {
      onDone?.();
      return;
    }
    window.speechSynthesis.cancel();
    const hostId = resolveHostForItem(item);
    const u = new SpeechSynthesisUtterance(item.sentence);
    const voice = pickVoice(voicesRef.current, hostId === 'liam' ? 'male' : 'female');
    if (voice) u.voice = voice;
    u.rate = stateRef.current.rate;
    u.pitch = 1;
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      if (session !== sessionRef.current || !stateRef.current.playing) return;
      onDone?.();
    };
    u.onend = finish;
    u.onerror = finish;
    window.speechSynthesis.speak(u);
  }, [browserSupported, resolveHostForItem]);

  const speakBrowserFrom = useCallback((startIdx, session) => {
    const queue = queueRef.current;
    if (!browserSupported || queue.length === 0) return;
    window.speechSynthesis.cancel();
    if (gapTimerRef.current) { clearTimeout(gapTimerRef.current); gapTimerRef.current = null; }

    const speakOne = (k) => {
      if (session !== sessionRef.current || !stateRef.current.playing) return;
      if (k >= queue.length) {
        stateRef.current.playing = false;
        setStatus('idle');
        setIdx(0);
        setIntra(0);
        return;
      }
      stateRef.current.idx = k;
      setIdx(k);
      setIntra(0);
      const item = queue[k];
      speakBrowserSentence(item, session, () => {
        if (session !== sessionRef.current || !stateRef.current.playing) return;
        const nextItem = queue[k + 1];
        const hostChange = nextItem && resolveHostForItem(nextItem) !== resolveHostForItem(item);
        const sectionChange = nextItem && nextItem.sectionIdx !== item.sectionIdx;
        const gap = (sectionChange || hostChange) ? SECTION_GAP_MS : SENTENCE_GAP_MS;
        gapTimerRef.current = setTimeout(() => speakOne(k + 1), gap / stateRef.current.rate);
      });
    };
    speakOne(startIdx);
  }, [browserSupported, speakBrowserSentence, resolveHostForItem]);

  const speakAzureFrom = useCallback((startIdx, session) => {
    const queue = queueRef.current;
    if (queue.length === 0) return;
    stopAudioElement();
    if (gapTimerRef.current) { clearTimeout(gapTimerRef.current); gapTimerRef.current = null; }

    const speakOne = async (k) => {
      if (session !== sessionRef.current || !stateRef.current.playing) return;
      if (k >= queue.length) {
        stateRef.current.playing = false;
        setStatus('idle');
        setIdx(0);
        setIntra(0);
        return;
      }
      stateRef.current.idx = k;
      setIdx(k);
      setIntra(0);

      const item = queue[k];
      const hostId = resolveHostForItem(item);
      const sentence = item.sentence;
      const nextItem = queue[k + 1];
      if (nextItem?.sentence) prefetchAzure(nextItem.sentence, resolveHostForItem(nextItem));

      const advance = () => {
        if (session !== sessionRef.current || !stateRef.current.playing) return;
        const nxt = queue[k + 1];
        const hostChange = nxt && resolveHostForItem(nxt) !== hostId;
        const sectionChange = nxt && nxt.sectionIdx !== item.sectionIdx;
        const gap = (sectionChange || hostChange) ? SECTION_GAP_MS : SENTENCE_GAP_MS;
        gapTimerRef.current = setTimeout(() => speakOne(k + 1), gap / stateRef.current.rate);
      };

      let url;
      try {
        url = await fetchAzureUrl(sentence, hostId);
      } catch (err) {
        // Fall back for THIS sentence only, then resume Azure for the next ones.
        // (Previously speakBrowserFrom took over the rest of the guide — that made
        // Liam turns collapse into one browser voice that sounded like Clara.)
        console.warn('[AudioGuide] Azure synthesize failed, one-sentence browser fallback:', err?.message || err);
        if (session !== sessionRef.current || !stateRef.current.playing) return;
        if (browserSupported) {
          speakBrowserSentence(item, session, advance);
        } else {
          advance();
        }
        return;
      }

      if (session !== sessionRef.current || !stateRef.current.playing) return;

      const audio = new Audio(url);
      audioRef.current = audio;
      audio.playbackRate = stateRef.current.rate;
      audio.ontimeupdate = () => {
        if (!audio.duration || !Number.isFinite(audio.duration)) return;
        setIntra(Math.min(1, audio.currentTime / audio.duration));
      };

      let advanced = false;
      const onceAdvance = () => {
        if (advanced) return;
        advanced = true;
        advance();
      };
      audio.onended = onceAdvance;
      audio.onerror = onceAdvance;

      try {
        await audio.play();
      } catch (err) {
        console.warn('[AudioGuide] Audio play failed:', err?.message || err);
        onceAdvance();
      }
    };

    speakOne(startIdx);
  }, [stopAudioElement, prefetchAzure, fetchAzureUrl, browserSupported, speakBrowserSentence, resolveHostForItem]);

  const speakFrom = useCallback((startIdx) => {
    const queue = queueRef.current;
    if (!supported || queue.length === 0) return;
    const session = ++sessionRef.current;
    if (browserSupported) window.speechSynthesis.cancel();
    stopAudioElement();
    if (gapTimerRef.current) { clearTimeout(gapTimerRef.current); gapTimerRef.current = null; }

    const i = Math.max(0, Math.min(startIdx, queue.length - 1));
    stateRef.current.idx = i;
    stateRef.current.playing = true;
    setIdx(i);
    setIntra(0);
    setStatus('playing');
    setVisible(true);

    if (stateRef.current.azure) {
      speakAzureFrom(i, session);
    } else if (browserSupported) {
      speakBrowserFrom(i, session);
    }
  }, [supported, browserSupported, stopAudioElement, speakAzureFrom, speakBrowserFrom]);

  const startGuide = useCallback((guideDef, { fromSectionIdx = 0 } = {}) => {
    if (!supported || !guideDef || !Array.isArray(guideDef.sections)) return;
    // Normalize speech text, then expand into NotebookLM-style Clara/Liam turns.
    const prepared = guideDef.sections.map((s) => {
      if (Array.isArray(s.turns) && s.turns.length) {
        return {
          ...s,
          turns: s.turns.map((t) => ({ ...t, speech: normalizeSpeech(t.speech) })),
        };
      }
      return { ...s, speech: normalizeSpeech(s.speech) };
    });
    const mode = stateRef.current.hostMode || 'duo';
    const { sections: expanded, queue: q } = buildDualHostQueue(prepared, mode);
    if (q.length === 0) return;
    clearBlobCache();
    queueRef.current = q;
    const guideWithSections = {
      ...guideDef,
      // Keep pristine source so host-mode switches re-expand cleanly.
      sourceSections: prepared,
      sections: expanded.map((s) => ({
        title: s.title,
        icon: s.icon,
        speech: s.turns.map((t) => t.speech).join(' '),
        turns: s.turns,
      })),
    };
    setGuide(guideWithSections);
    let k = 0;
    if (fromSectionIdx > 0) {
      const found = q.findIndex((item) => item.sectionIdx === fromSectionIdx);
      k = found >= 0 ? found : 0;
    }
    speakFrom(k);
  }, [supported, clearBlobCache, speakFrom]);

  const pause = useCallback(() => {
    if (!supported) return;
    sessionRef.current += 1;
    stateRef.current.playing = false;
    if (gapTimerRef.current) { clearTimeout(gapTimerRef.current); gapTimerRef.current = null; }
    if (browserSupported) window.speechSynthesis.cancel();
    stopAudioElement();
    setStatus('paused');
  }, [supported, browserSupported, stopAudioElement]);

  const play = useCallback(() => speakFrom(stateRef.current.idx || 0), [speakFrom]);

  const stop = useCallback(() => {
    if (!supported) return;
    sessionRef.current += 1;
    stateRef.current.playing = false;
    if (gapTimerRef.current) { clearTimeout(gapTimerRef.current); gapTimerRef.current = null; }
    if (browserSupported) window.speechSynthesis.cancel();
    stopAudioElement();
    setStatus('idle');
    setIdx(0);
    setIntra(0);
  }, [supported, browserSupported, stopAudioElement]);

  const close = useCallback(() => { stop(); setVisible(false); clearBlobCache(); }, [stop, clearBlobCache]);
  const next = useCallback(() => speakFrom(stateRef.current.idx + 1), [speakFrom]);
  const prev = useCallback(() => speakFrom(stateRef.current.idx - 1), [speakFrom]);
  const seek = useCallback((fraction) => {
    speakFrom(Math.floor(Math.max(0, Math.min(0.999, fraction)) * queueRef.current.length));
  }, [speakFrom]);
  const playSection = useCallback((sectionIdx) => {
    const k = queueRef.current.findIndex((q) => q.sectionIdx === sectionIdx);
    if (k >= 0) speakFrom(k);
  }, [speakFrom]);

  const toggleGender = useCallback(() => {
    setHostMode((mode) => {
      const order = ['duo', 'clara', 'liam'];
      const nextMode = order[(order.indexOf(mode) + 1) % order.length];
      stateRef.current.hostMode = nextMode;
      savePrefs({
        ...loadPrefs(),
        hostMode: nextMode,
        gender: nextMode === 'liam' ? 'male' : 'female',
        voiceName: nextMode === 'liam' ? AZURE_VOICES.male.name : (nextMode === 'clara' ? AZURE_VOICES.female.name : null),
        lang: 'en',
      });
      return nextMode;
    });
  }, []);

  const setVoice = useCallback((modeOrName) => {
    let nextMode = 'duo';
    if (modeOrName === 'duo' || modeOrName === 'clara' || modeOrName === 'liam') {
      nextMode = modeOrName;
    } else if (modeOrName === AZURE_VOICES.male.name) {
      nextMode = 'liam';
    } else if (modeOrName === AZURE_VOICES.female.name) {
      nextMode = 'clara';
    }
    setHostMode(nextMode);
    stateRef.current.hostMode = nextMode;
    savePrefs({
      ...loadPrefs(),
      hostMode: nextMode,
      gender: nextMode === 'liam' ? 'male' : 'female',
      voiceName: nextMode === 'liam' ? AZURE_VOICES.male.name : (nextMode === 'clara' ? AZURE_VOICES.female.name : null),
      lang: 'en',
    });
  }, []);

  const cycleRate = useCallback(() => {
    setRate((r) => {
      const order = [0.85, 1, 1.15, 1.3];
      const nr = order[(order.indexOf(r) + 1) % order.length];
      stateRef.current.rate = nr;
      savePrefs({ ...loadPrefs(), rate: nr, lang: 'en' });
      if (audioRef.current) {
        try { audioRef.current.playbackRate = nr; } catch { /* ignore */ }
      }
      return nr;
    });
  }, []);

  // Host-mode changes rebuild the current guide so duo/solo takes effect immediately.
  useEffect(() => {
    if (status !== 'playing' && status !== 'paused') return;
    if (!guide) return;
    const source = guide.sourceSections || guide.sections;
    const { queue: q } = buildDualHostQueue(source, hostMode);
    if (!q.length) return;
    const keepSection = queueRef.current[stateRef.current.idx]?.sectionIdx ?? 0;
    clearBlobCache();
    queueRef.current = q;
    const k = q.findIndex((item) => item.sectionIdx === keepSection);
    const startAt = k >= 0 ? k : 0;
    if (status === 'playing') speakFrom(startAt);
    else {
      setIdx(startAt);
      setIntra(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostMode]);

  useEffect(() => {
    if (status !== 'playing' || azureConfigured) return;
    speakFrom(stateRef.current.idx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rate]);

  useEffect(() => () => {
    sessionRef.current += 1;
    if (browserSupported) window.speechSynthesis.cancel();
    stopAudioElement();
    clearBlobCache();
    if (gapTimerRef.current) clearTimeout(gapTimerRef.current);
  }, [browserSupported, stopAudioElement, clearBlobCache]);

  const queue = queueRef.current;
  const current = queue[idx] || null;
  const sections = guide ? guide.sections : [];
  const currentSection = current ? sections[current.sectionIdx] : null;
  const currentSpeaker = current?.speaker || null;

  const hostModeLabel = hostMode === 'duo' ? 'Clara & Liam' : (hostMode === 'liam' ? 'Liam' : 'Clara');
  const voiceName = currentSpeaker
    ? speakerLabel(currentSpeaker)
    : hostModeLabel;
  const activeVoice = hostMode;
  const gender = currentSpeaker === 'liam' || hostMode === 'liam' ? 'male' : 'female';

  const value = useMemo(() => ({
    supported,
    engine: azureConfigured ? 'azure' : 'browser',
    status,
    visible,
    rate,
    gender,
    hostMode,
    lang: 'en',
    activeGuideId: guide ? guide.id : null,
    guideTitle: guide ? guide.title : '',
    accent: (guide && guide.accent) || DEFAULT_ACCENT,
    progress: queue.length ? (idx + intra) / queue.length : 0,
    currentSectionIdx: current ? current.sectionIdx : 0,
    currentSectionTitle: currentSection ? currentSection.title : '',
    currentSectionIcon: (currentSection && currentSection.icon) || '🔊',
    currentSentence: current ? current.sentence : '',
    currentSpeaker,
    voiceName,
    activeVoice,
    voiceOptions,
    startGuide, play, pause, stop, close, next, prev, seek, playSection, toggleGender, cycleRate, setVoice,
  }), [supported, azureConfigured, status, visible, rate, gender, hostMode, guide, queue.length, idx, intra, current,
    currentSection, currentSpeaker, voiceName, activeVoice, voiceOptions,
    startGuide, play, pause, stop, close, next, prev, seek, playSection, toggleGender, cycleRate, setVoice]);

  return (
    <AudioGuideContext.Provider value={value}>
      {children}
      <NarratorPlayer />
    </AudioGuideContext.Provider>
  );
}

export const useAudioGuide = () => {
  const ctx = useContext(AudioGuideContext);
  return ctx || {
    supported: false, engine: 'browser', status: 'idle', visible: false, rate: 1, gender: 'female', hostMode: 'duo', lang: 'en',
    activeGuideId: null, guideTitle: '', accent: DEFAULT_ACCENT, progress: 0,
    currentSectionIdx: 0, currentSectionTitle: '', currentSectionIcon: '🔊', currentSentence: '', currentSpeaker: null, voiceName: null,
    activeVoice: null, voiceOptions: [],
    startGuide: () => {}, play: () => {}, pause: () => {}, stop: () => {}, close: () => {},
    next: () => {}, prev: () => {}, seek: () => {}, playSection: () => {}, toggleGender: () => {}, cycleRate: () => {}, setVoice: () => {},
  };
};

/* ------------------------------------------------------------------ */
/* Floating mini-player                                                */
/* ------------------------------------------------------------------ */

const PlayerBtn = ({ onClick, title, children, big = false, accent }) => (
  <button
    onClick={onClick}
    title={title}
    style={{
      width: big ? 42 : 30, height: big ? 42 : 30, borderRadius: '50%',
      border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: big ? 'white' : 'rgba(255,255,255,0.16)', color: big ? accent : 'white',
      fontSize: big ? '1.05rem' : '0.8rem', fontWeight: 700, flexShrink: 0,
      boxShadow: big ? '0 2px 8px rgba(0,0,0,0.25)' : 'none',
    }}
  >{children}</button>
);

const NarratorPlayer = () => {
  const g = useContext(AudioGuideContext);
  const barRef = useRef(null);
  const [showVoices, setShowVoices] = useState(false);
  if (!g || !g.supported || !g.visible) return null;
  const playing = g.status === 'playing';
  const accent = g.accent;
  const isAzure = g.engine === 'azure';

  const onBarClick = (e) => {
    const rect = barRef.current.getBoundingClientRect();
    g.seek((e.clientX - rect.left) / rect.width);
  };

  return (
    <div style={{
      position: 'fixed', bottom: 18, right: 18, zIndex: 10002, width: 340,
      background: `linear-gradient(135deg, ${accent} 0%, ${
        (() => {
          const a = String(accent || '').toLowerCase();
          if (a === '#fff' || a === '#ffffff') return '#b91c1c';
          if (a === '#b91c1c' || a === '#dc2626' || a === '#dc3545') return '#7f1d1d';
          return '#1e293b';
        })()
      } 130%)`,
      borderRadius: 16, padding: '12px 14px 10px', color: 'white',
      boxShadow: '0 10px 34px rgba(0,0,0,0.4)',
      fontFamily: 'inherit',
    }}>
      {showVoices && (
        <div style={{
          position: 'absolute', bottom: 'calc(100% + 8px)', right: 0, width: 260,
          background: 'white', borderRadius: 12, boxShadow: '0 12px 34px rgba(0,0,0,0.35)',
          padding: '6px', zIndex: 1,
        }}>
          <div style={{ fontSize: '0.66rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', padding: '4px 8px' }}>
            Hosts
          </div>
          {g.voiceOptions.map((v) => (
            <button
              key={v.id}
              onClick={() => { g.setVoice(v.id); setShowVoices(false); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                padding: '7px 8px', border: 'none', borderRadius: 8, cursor: 'pointer', textAlign: 'left',
                background: g.activeVoice === v.id ? `${accent}14` : 'transparent',
                color: '#1e293b', fontSize: '0.78rem', fontWeight: g.activeVoice === v.id ? 700 : 500,
              }}
            >
              <span>{v.id === 'duo' ? '🎙️' : (v.gender === 'male' ? '👨' : '👩')}</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.label}</span>
                {v.hint && (
                  <span style={{ display: 'block', fontSize: '0.62rem', color: '#94a3b8', fontWeight: 500 }}>{v.hint}</span>
                )}
              </span>
              {isAzure && (
                <span style={{ fontSize: '0.6rem', fontWeight: 700, color: '#059669', background: '#ecfdf5', borderRadius: 999, padding: '1px 7px', flexShrink: 0 }}>
                  neural
                </span>
              )}
              {g.activeVoice === v.id && <span style={{ color: accent, flexShrink: 0 }}>✓</span>}
            </button>
          ))}
          <div style={{ fontSize: '0.62rem', color: '#94a3b8', padding: '6px 8px 4px', borderTop: '1px solid #f1f5f9', marginTop: 4 }}>
            {isAzure
              ? 'Azure neural · Clara & Liam (Canadian English). French coming later.'
              : 'Azure unavailable — using closest browser female/male voices.'}
          </div>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: '1.05rem' }}>{g.currentSectionIcon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {g.currentSpeaker ? `${speakerLabel(g.currentSpeaker)} · ` : ''}{g.currentSectionTitle || g.guideTitle}
          </div>
          <div style={{ fontSize: '0.66rem', opacity: 0.75, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {g.currentSentence}
          </div>
        </div>
        <button onClick={g.close} title="Close player"
          style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.75)', cursor: 'pointer', fontSize: '1rem', lineHeight: 1, padding: 2 }}>✕</button>
      </div>

      <div ref={barRef} onClick={onBarClick} title="Jump"
        style={{ height: 14, display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: 7 }}>
        <div style={{ position: 'relative', height: 4, borderRadius: 999, background: 'rgba(255,255,255,0.22)', width: '100%' }}>
          <div style={{ position: 'absolute', inset: 0, width: `${Math.round(g.progress * 100)}%`, borderRadius: 999, background: 'white', transition: 'width .25s linear' }} />
          <div style={{
            position: 'absolute', top: '50%', left: `${Math.round(g.progress * 100)}%`,
            transform: 'translate(-50%, -50%)', width: 11, height: 11, borderRadius: '50%',
            background: 'white', boxShadow: '0 1px 4px rgba(0,0,0,0.35)', transition: 'left .25s linear',
          }} />
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <PlayerBtn onClick={g.prev} title="Previous sentence" accent={accent}>⏮</PlayerBtn>
        <PlayerBtn big onClick={playing ? g.pause : g.play} title={playing ? 'Pause' : 'Play'} accent={accent}>
          {playing ? '⏸' : '▶'}
        </PlayerBtn>
        <PlayerBtn onClick={g.next} title="Next sentence" accent={accent}>⏭</PlayerBtn>
        <button onClick={g.cycleRate} title="Playback speed"
          style={{ background: 'rgba(255,255,255,0.16)', border: 'none', color: 'white', cursor: 'pointer', borderRadius: 999, padding: '4px 10px', fontSize: '0.7rem', fontWeight: 700 }}>
          {g.rate}×
        </button>
        <button onClick={() => setShowVoices((s) => !s)} title={g.hostMode === 'duo' ? 'Dual host: Clara & Liam — click to change' : `Host: ${g.hostMode === 'liam' ? 'Liam' : 'Clara'} only — click to change`}
          style={{ background: showVoices ? 'rgba(255,255,255,0.32)' : 'rgba(255,255,255,0.16)', border: 'none', color: 'white', cursor: 'pointer', borderRadius: 999, padding: '4px 10px', fontSize: '0.7rem', fontWeight: 700, marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5, maxWidth: 140 }}>
          <span>{g.hostMode === 'duo' ? '🎙️' : (g.hostMode === 'liam' ? '👨' : '👩')}</span>
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {g.hostMode === 'duo'
              ? (g.currentSpeaker ? speakerLabel(g.currentSpeaker) : 'Clara & Liam')
              : (g.hostMode === 'liam' ? 'Liam' : 'Clara')}
          </span>
          <span style={{ fontSize: '0.55rem', opacity: 0.8 }}>▲</span>
        </button>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* ListenButton                                                        */
/* ------------------------------------------------------------------ */

const findContentRoot = (buttonEl) => {
  let el = buttonEl ? buttonEl.parentElement : null;
  while (el && el !== document.body) {
    if ((el.textContent || '').length > 300) return el;
    el = el.parentElement;
  }
  return null;
};

/**
 * Props:
 *   guideId    (required) unique id, e.g. 'bid-log-help'
 *   title      guide title shown in the player
 *   accent     page accent colour for the player + button
 *   sections   [{title, icon?, turns:[{speaker,speech}]}] or legacy {speech}
 *   getContent () => HTMLElement — extract narration from a specific element
 *   (neither)  — auto-detects the surrounding modal content from the DOM
 *   dark       set true when placed on a dark/coloured header
 *   compact    icon-only round button (for tight headers)
 *   style      extra style overrides
 */
export const ListenButton = ({ guideId, title = 'Help guide', accent = DEFAULT_ACCENT, sections, getContent, dark = false, compact = false, style }) => {
  const g = useAudioGuide();
  const btnRef = useRef(null);
  if (!g.supported) return null;

  const isActive = g.activeGuideId === guideId;
  const playing = isActive && g.status === 'playing';

  const onClick = () => {
    if (playing) { g.pause(); return; }
    if (isActive && g.status === 'paused') { g.play(); return; }
    let secs = sections;
    if (!secs) {
      const curated = getHelpNarration(guideId);
      if (curated) secs = curated.sections || curated;
    }
    if (!secs) {
      const root = getContent ? getContent() : findContentRoot(btnRef.current);
      secs = extractSectionsFromElement(root, title);
    }
    if (!secs || !secs.length) return;
    g.startGuide({ id: guideId, title, accent, sections: secs });
  };

  if (compact) {
    return (
      <button
        ref={btnRef}
        onClick={onClick}
        title={playing ? 'Pause narration' : 'Read this help aloud — keeps playing while you work'}
        style={{
          width: 30, height: 30, borderRadius: '50%', cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.85rem', flexShrink: 0,
          background: dark ? 'rgba(255,255,255,0.18)' : `${accent}12`,
          color: dark ? 'white' : accent,
          border: dark ? '1px solid rgba(255,255,255,0.35)' : `1px solid ${accent}44`,
          ...style,
        }}
      >{playing ? '⏸' : '🎧'}</button>
    );
  }

  return (
    <button
      ref={btnRef}
      onClick={onClick}
      title={playing ? 'Pause narration' : 'Read this help aloud — keeps playing while you work'}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '5px 12px', borderRadius: 999, cursor: 'pointer',
        fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap',
        background: dark ? 'rgba(255,255,255,0.18)' : `${accent}12`,
        color: dark ? 'white' : accent,
        border: dark ? '1px solid rgba(255,255,255,0.35)' : `1px solid ${accent}44`,
        ...style,
      }}
    >
      <span>{playing ? '⏸' : '🎧'}</span>
      <span>{playing ? 'Pause' : 'Listen'}</span>
    </button>
  );
};
